import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { quoteResultsV2CSS } from './QuoteResultsV2.css.js';
import { ResidualMatrixTable } from './ResidualMatrixTable';
import { PlazoComparisonTable } from './PlazoComparisonTable';
import { InitialPaymentTable } from './InitialPaymentTable';
import { FloatingQuoteCTA } from './FloatingQuoteCTA';
import { formatNumber } from '../../../../../Utils';
import { parseMoney } from './pmt.js';

function buildFormSignature(state) {
    return JSON.stringify({
        amount: state?.rodac?.amount,
        accesorios: state?.rodac?.accesorios,
        enganche: state?.rodac?.enganche,
        paymentCfg: state?.rodac?.payment_configuration_id,
        insurance: state?.rodac?.insurance,
        residual: state?.residualValue?.id,
        typeCoverage: state?.typeCoverage?.id,
        description: state?.rodac?.description_id,
        carStatus: state?.rodac?.car_status_id,
    });
}

function formatMoney(n) {
    if (!Number.isFinite(n)) return '—';
    return `$${formatNumber(n.toFixed(2))}`;
}

function QuoteResultsV2({ state, onRequest, isCotizador3 = false }) {
    const hasResults = !!(state?.pdfPlazo && state.pdfPlazo.length > 0);

    const snapshotRef = useRef(null);
    const [displaySnapshot, setDisplaySnapshot] = useState(null);
    const [phase, setPhase] = useState('idle');
    const triggerRef = useRef(null);
    const headerRef = useRef(null);

    const currentSignature = useMemo(() => buildFormSignature(state), [state]);

    useEffect(() => {
        if (hasResults) {
            snapshotRef.current = {
                state,
                signature: currentSignature,
            };
            setDisplaySnapshot(snapshotRef.current);
            setPhase('visible');
        }
    }, [hasResults, state?.pdfPlazo]);

    useEffect(() => {
        if (phase === 'visible' && headerRef.current) {
            const timer = setTimeout(() => {
                headerRef.current?.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start',
                });
            }, 500);
            return () => clearTimeout(timer);
        }
    }, [phase, displaySnapshot?.signature]);

    useEffect(() => {
        if (phase !== 'visible' || !displaySnapshot) return;
        if (currentSignature !== displaySnapshot.signature) {
            setPhase('dissolving');
            const t = setTimeout(() => {
                setDisplaySnapshot(null);
                setPhase('idle');
            }, 900);
            return () => clearTimeout(t);
        }
    }, [currentSignature, phase, displaySnapshot]);

    const isPure = !!displaySnapshot?.state?.resumen?.isPure;
    const showResidualMatrix = isPure && !isCotizador3;

    const summary = useMemo(() => {
        const snap = displaySnapshot?.state;
        if (!snap) return null;
        const plazo = (snap.pdfPlazo || []).find(p => p.selected) || (snap.pdfPlazo || []).find(p => !p.ignore) || snap.pdfPlazo?.[0];
        const residual = isCotizador3 ? undefined : snap.residualValue?.percentage;
        const V = (Number(snap?.rodac?.amount) || 0) + (Number(snap?.rodac?.accesorios) || 0);
        const totalInicial = parseMoney(
            plazo?.section2?.['Total Pago Inicial (incl IVA)']?.[1] ||
            plazo?.section2?.['Total Pago Inicial (incl IVA)']?.[0]
        );
        const rentaActual = parseMoney(
            plazo?.section3?.['Total Pago Mensual (incl IVA)']?.[1] ||
            plazo?.section3?.['Pago Mensual Total (incl IVA)']?.[1] ||
            plazo?.section3?.['Total Pago Mensual (incl IVA)']?.[0]
        );
        return {
            V,
            residual,
            totalInicial,
            rentaActual,
            plazoActual: plazo?.plazo,
            marca: snap.resumen?.Marca,
            modelo: snap.resumen?.Modelo,
            anio: snap.resumen?.Año,
        };
    }, [displaySnapshot]);

    const show = phase !== 'idle' && !!displaySnapshot;

    return (
        <div css={quoteResultsV2CSS()}>
            <AnimatePresence>
                {show && (
                    <motion.div
                        key="outer"
                        className="ResultsOuter"
                        initial={{ height: 0, opacity: 0 }}
                        animate={
                            phase === 'dissolving'
                                ? {
                                      height: 0,
                                      opacity: 0,
                                      transition: {
                                          height: { duration: 0.55, delay: 0.25, ease: [0.22, 1, 0.36, 1] },
                                          opacity: { duration: 0.4, ease: 'easeIn' },
                                      },
                                  }
                                : {
                                      height: 'auto',
                                      opacity: 1,
                                      transition: {
                                          height: { duration: 0.55, ease: [0.22, 1, 0.36, 1] },
                                          opacity: { duration: 0.4, delay: 0.15 },
                                      },
                                  }
                        }
                        exit={{
                            height: 0,
                            opacity: 0,
                            transition: {
                                height: { duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] },
                                opacity: { duration: 0.35, ease: 'easeIn' },
                            },
                        }}
                    >
                    <div className="ResultsInner">
                    <motion.div
                        key="results"
                        initial={{ opacity: 0, y: 40, scale: 0.98 }}
                        animate={
                            phase === 'dissolving'
                                ? {
                                      opacity: 0,
                                      filter: 'blur(12px)',
                                      y: -20,
                                      scale: 0.96,
                                      transition: { duration: 0.5, ease: 'easeIn' },
                                  }
                                : { opacity: 1, y: 0, scale: 1, filter: 'blur(0px)' }
                        }
                        transition={{
                            type: 'spring',
                            stiffness: 180,
                            damping: 24,
                            mass: 0.9,
                        }}
                        className="ResultsShell"
                    >
                        <div className="ResultsHeader" ref={headerRef}>
                            <div>
                                <h2>Tu cotización está lista</h2>
                                <div className="hint">
                                    Revisa las opciones · elige la que más te convenga
                                </div>
                            </div>
                        </div>

                        {summary && (
                            <div className="QuoteSummaryBar">
                                <div className="SummaryItem">
                                    <span className="label">Vehículo</span>
                                    <span className="value">
                                        {[summary.marca, summary.modelo, summary.anio]
                                            .filter(Boolean)
                                            .join(' · ') || '—'}
                                    </span>
                                </div>
                                <div className="SummaryItem">
                                    <span className="label">Precio total</span>
                                    <span className="value">{formatMoney(summary.V)}</span>
                                </div>
                                {showResidualMatrix && summary.residual !== undefined && (
                                    <div className="SummaryItem">
                                        <span className="label">Residual actual</span>
                                        <span className="value">{summary.residual}%</span>
                                    </div>
                                )}
                                <div className="SummaryItem">
                                    <span className="label">Pago inicial</span>
                                    <span className="value">
                                        {formatMoney(summary.totalInicial)}
                                    </span>
                                </div>
                            </div>
                        )}

                        <div className="TablesGrid">
                            <div ref={triggerRef}>
                                {showResidualMatrix ? (
                                    <ResidualMatrixTable state={displaySnapshot.state} />
                                ) : (
                                    <PlazoComparisonTable state={displaySnapshot.state} isCotizador3={isCotizador3} />
                                )}
                            </div>
                            <InitialPaymentTable state={displaySnapshot.state} isCotizador3={isCotizador3} />
                        </div>
                    </motion.div>
                    </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <FloatingQuoteCTA
                triggerRef={triggerRef}
                onClick={onRequest}
                visible={phase === 'visible'}
            />
        </div>
    );
}

export { QuoteResultsV2 };
